import React from 'react';

import { getDateWithoutTimezoneShift } from '@lender/utils';
import {
  DateIcon,
  DollarIcon,
  PercentageIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATE_FORMAT, COMMON_DATETIME_FORMAT } from 'constants/date';
import {
  accountingFormatOptions,
  fixedTwoFractionDigitsExceptIntegerCurrencyOptions,
} from 'constants/loanApplication';
import { StatusColor, StatusEnum } from 'constants/status';
import { formatNumberToCurrencyString, formatNumberToFixed2DecimalsExceptNull } from 'utils/number';
import { getModifiedFieldChangeKind } from 'utils/pendingChangeKind';
import { getFieldPendingChangeAuthors } from 'utils/pendingChangesAuthors';

import { commonTooltipStyles } from './LoanFeesHead.styles';

const currencyOptions = {
  ...fixedTwoFractionDigitsExceptIntegerCurrencyOptions,
  ...accountingFormatOptions,
};

export const getSummaryInfoConfig = ({
  closingDate,
  fundingDate,
  firstPaymentDate,
  cashToCloseData,
  finalCashToClose,
  cureData,
  totalCure,
  apr,
  savedApr,
  modifiedFields,
  modifiedFieldDetails,
  loanFieldPendingChangeAuthors = {},
}) => {
  const pendingFields = { modifiedFields, modifiedFieldDetails };
  const aprView = !_.isNil(apr) ? `${formatNumberToFixed2DecimalsExceptNull(apr)}%` : '-';
  const savedAprView = !_.isNil(savedApr)
    ? `${formatNumberToFixed2DecimalsExceptNull(savedApr)}%`
    : '-';

  return [
    {
      name: 'Closing Date and Time',
      value: closingDate ? dayjs(closingDate).format(COMMON_DATETIME_FORMAT) : '-',
      icon: <DateIcon />,
      changeKind: getModifiedFieldChangeKind(pendingFields, 'closingDate'),
      pendingChangeAuthors: getFieldPendingChangeAuthors(
        loanFieldPendingChangeAuthors,
        'closingDate',
      ),
    },
    {
      name: 'Est Funding Date',
      value: fundingDate
        ? dayjs(getDateWithoutTimezoneShift(fundingDate)).format(COMMON_DATE_FORMAT)
        : '-',
      icon: <DateIcon />,
      changeKind: getModifiedFieldChangeKind(pendingFields, 'fundingDate'),
      pendingChangeAuthors: getFieldPendingChangeAuthors(
        loanFieldPendingChangeAuthors,
        'fundingDate',
      ),
    },
    {
      name: 'First Payment Date',
      value: firstPaymentDate
        ? dayjs(getDateWithoutTimezoneShift(firstPaymentDate)).format(COMMON_DATE_FORMAT)
        : '-',
      icon: <DateIcon />,
      changeKind: getModifiedFieldChangeKind(pendingFields, 'firstPaymentDate'),
      pendingChangeAuthors: getFieldPendingChangeAuthors(
        loanFieldPendingChangeAuthors,
        'firstPaymentDate',
      ),
    },
    {
      name: 'Cash From/To Borrower',
      value: !_.isNil(finalCashToClose)
        ? formatNumberToCurrencyString(finalCashToClose, currencyOptions)
        : '-',
      icon: <DollarIcon />,
      iconColor:
        !_.isNil(finalCashToClose) && finalCashToClose > 0
          ? StatusColor[StatusEnum.ERROR]
          : StatusColor[StatusEnum.SUCCESS],
      withInfoIcon: true,
      disableInteractive: false,
      hoverContent: cashToCloseData,
      customTooltipStyles: commonTooltipStyles,
      changeKind: getModifiedFieldChangeKind(pendingFields, 'cashToCloseDetailedSummary'),
    },
    {
      name: 'Cure Amount',
      value: !_.isNil(totalCure) ? formatNumberToCurrencyString(totalCure, currencyOptions) : '-',
      icon: <DollarIcon />,
      iconColor:
        _.isNil(totalCure) || totalCure === 0
          ? StatusColor[StatusEnum.SUCCESS]
          : StatusColor[StatusEnum.ERROR],
      withInfoIcon: true,
      disableInteractive: false,
      hoverContent: cureData,
      customTooltipStyles: commonTooltipStyles,
      changeKind: getModifiedFieldChangeKind(pendingFields, 'cureSummary'),
    },
    {
      name: 'APR: Current/Previous',
      value: `${aprView} / ${savedAprView}`,
      icon: <PercentageIcon />,
      changeKind: getModifiedFieldChangeKind(pendingFields, ['apr', 'savedApr']),
    },
  ];
};
